/**
 * Reusable "split column": one parent header with several sub-columns, all
 * reading from the same row field.
 *
 *   DISTANCE
 *   KMs | Count
 *
 * The row value can be an object ({distance, count}) or a plain number.
 */

/** One sub-column: the header text and the row field it reads. */
export type SplitLabel = {label: string; uid: string; width?: number};

/**
 * One entry per split column:
 *   table    the table title (reports: "Header ( Menu ) " with trailing space)
 *   column   "all", the uid to split, a list of uids sharing the same labels, or a RegExp
 *            for dynamic columns (e.g. one column per date)
 *   exclude  with column "all": the uids that stay unsplit
 *   labels   its sub-columns: label = header name, uid = field in the row value
 */
export type SplitRule = {
  table: string;
  column: "all" | string | string[] | RegExp;
  /** columns to leave unsplit, used with column: "all" */
  exclude?: string[];
  labels: SplitLabel[];
};

export const SPLIT_COLUMNS: SplitRule[] = [
  // {
  //   table: "Performance Reports ( Speed ) ",
  //   column: [
  //     "below40Kmph",
  //     "between40To50Kmph",
  //     "between50To60Kmph",
  //     "between60To70Kmph",
  //     "between70To80Kmph",
  //     "between80To90Kmph",
  //     "above90Kmph"
  //   ],
  //   labels: [
  //     {label: "KMs", uid: "distance"},
  //     {label: "Count", uid: "count"}
  //   ]
  // },
  // {
  //   table: "Performance Reports ( Distance ) ",
  //   // every column splits except the ones listed in exclude
  //   // (the date columns are dynamic: each value should be {schedule, unschedule, total})
  //   column: "all",
  //   exclude: ["trackerId", "name", "totalKm"],
  //   labels: [
  //     {label: "Schedule", uid: "schedule"},
  //     {label: "Unschedule", uid: "unschedule"},
  //     {label: "Total", uid: "total"}
  //   ]
  // }
];

const readSplit = (cell: any, split: SplitLabel, index: number) => {
  if (cell !== null && typeof cell === "object" && !Array.isArray(cell)) {
    return cell[split.uid] ?? 0;
  }
  // plain number: first sub-column shows it as is, the others have no data
  return index === 0 ? (cell ?? 0) : 0;
};

/** The sub-columns for this table + column, or null when it is not split. */
export const getSplits = (col: any, tableTitle?: string): SplitLabel[] | null =>
  SPLIT_COLUMNS.find(
    r =>
      r.table === tableTitle &&
      !r.exclude?.includes(col?.uid) &&
      (r.column === "all"
        ? true
        : r.column instanceof RegExp
          ? r.column.test(String(col?.uid))
          : Array.isArray(r.column)
            ? r.column.includes(col?.uid)
            : r.column === col?.uid)
  )?.labels ?? null;

/** Builds the AG Grid column-group definition for a split column. */
export const buildSplitColumn = (
  col: any,
  headerName: string,
  splits: SplitLabel[]
) => ({
  headerName,
  marryChildren: true,
  children: splits.map((split, index) => ({
    headerName: split.label,
    colId: `${col.uid}_${split.uid}`,
    valueGetter: (params: any) =>
      readSplit(params.data?.[col.uid], split, index),
    filter: "agNumberColumnFilter",
    sortable: true,
    resizable: true,
    // wide enough for the label plus the sort/filter icons
    width: split.width ?? Math.max(100, split.label.length * 10 + 60)
  }))
});
